import React from "react";
import { AlertCircle, AlertTriangle, Info, X, CheckCircle } from "lucide-react";
import { Button } from "../ui/Button";
import { Badge } from "../ui/badge";
import { useTranslation } from "react-i18next";

interface WarningPopupProps {
  warning: {
    id: string;
    warningType: string;
    message: string;
    severity: string;
    pointsDeducted?: number;
    metadata?: Record<string, any>;
  };
  onDismiss: (warningId: string) => void;
  onClose: () => void;
}

const WarningPopup: React.FC<WarningPopupProps> = ({ warning, onDismiss, onClose }) => {
  const { t } = useTranslation();
  
  const getSeverityStyles = (severity: string) => {
    switch (severity) {
      case "critical":
        return {
          bg: "bg-red-50",
          border: "border-red-200",
          icon: <AlertCircle className="h-6 w-6 text-red-600" />,
          badge: "bg-red-100 text-red-700",
        };
      case "warning":
        return {
          bg: "bg-yellow-50",
          border: "border-yellow-200",
          icon: <AlertTriangle className="h-6 w-6 text-yellow-600" />,
          badge: "bg-yellow-100 text-yellow-700",
        };
      case "info":
        return {
          bg: "bg-blue-50",
          border: "border-blue-200",
          icon: <Info className="h-6 w-6 text-blue-600" />,
          badge: "bg-blue-100 text-blue-700",
        };
      default:
        return {
          bg: "bg-gray-50",
          border: "border-gray-200",
          icon: <Info className="h-6 w-6 text-gray-600" />,
          badge: "bg-gray-100 text-gray-700",
        };
    }
  };

  const getWarningTitle = (type: string) => {
    return t(`warningPopup.titles.${type}`, { defaultValue: t("warningPopup.titles.default") });
  };

  const styles = getSeverityStyles(warning.severity);

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-3 sm:p-4 md:p-6 animate-in fade-in duration-300">
      {/* Mobile: Full width with margins, Tablet: 90%, Desktop: max-w-md */}
      <div className="bg-white rounded-lg sm:rounded-xl w-full max-w-[calc(100%-1.5rem)] sm:max-w-lg md:max-w-md shadow-2xl animate-in zoom-in-95 duration-300 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className={`p-4 sm:p-5 md:p-6 border-b ${styles.border} ${styles.bg} rounded-t-lg sm:rounded-t-xl`}>
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-start gap-2 sm:gap-3 flex-1 min-w-0">
              <div className="mt-0.5 sm:mt-1 flex-shrink-0">{styles.icon}</div>
              <div className="flex-1 min-w-0">
                <h3 className="text-base sm:text-lg md:text-xl font-semibold text-gray-900 break-words">
                  {getWarningTitle(warning.warningType)}
                </h3>
                <Badge variant={undefined} className={`mt-1.5 sm:mt-2 text-xs ${styles.badge}`}>
                  {warning.severity.charAt(0).toUpperCase() + warning.severity.slice(1)}
                </Badge>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0 p-1 -mr-1"
            >
              <X className="h-4 w-4 sm:h-5 sm:w-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-5 md:p-6 space-y-3 sm:space-y-4">
          <p className="text-sm sm:text-base text-gray-700 leading-relaxed whitespace-pre-line break-words">
            {warning.message}
          </p>

          {warning.pointsDeducted && warning.pointsDeducted !== 0 && (
            <div
              className={`p-3 rounded-lg flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 ${
                warning.pointsDeducted > 0
                  ? "bg-green-50 border border-green-200"
                  : "bg-red-50 border border-red-200"
              }`}
            >
              <span className="text-xs sm:text-sm font-medium text-gray-700">
                {t("warningPopup.trustScoreImpact")}:
              </span>
              <span
                className={`text-base sm:text-lg font-bold ${
                  warning.pointsDeducted > 0 ? "text-green-600" : "text-red-600"
                }`}
              >
                {warning.pointsDeducted > 0 ? "+" : ""}
                {warning.pointsDeducted} {t("warningPopup.points")}
              </span>
            </div>
          )}

          {warning.warningType === "suspension_notice" && (
            <div className="p-3 sm:p-4 bg-orange-50 border border-orange-200 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-4 w-4 sm:h-5 sm:w-5 text-orange-600 mt-0.5 flex-shrink-0" />
                <div className="text-xs sm:text-sm text-gray-700">
                  <p className="font-medium mb-1 sm:mb-2">{t("warningPopup.importantNotes")}:</p>
                  <ul className="list-disc list-inside space-y-1 text-gray-600">
                    <li className="break-words">{t("warningPopup.suspensionNote1")}</li>
                    <li className="break-words">{t("warningPopup.suspensionNote2")}</li>
                    <li className="break-words">{t("warningPopup.suspensionNote3")}</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {warning.warningType === "trust_bonus" && (
            <div className="p-3 sm:p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-green-600 mt-0.5 flex-shrink-0" />
                <div className="text-xs sm:text-sm text-gray-700">
                  <p className="font-medium mb-1">{t("warningPopup.congratulations")}!</p>
                  <p className="text-gray-600 break-words">
                    {t("warningPopup.trustBonusMessage")}
                  </p>
                </div>
              </div>
            </div>
          )}

          {warning.severity === "critical" && warning.warningType !== "trust_bonus" && (
            <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
              <p className="text-xs text-gray-600 break-words">
                <strong>{t("warningPopup.important")}:</strong> {t("warningPopup.criticalWarning")}{" "}
                <a href="/terms" className="text-blue-600 hover:underline">
                  {t("warningPopup.termsOfService")}
                </a>
                .
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 sm:p-5 md:p-6 border-t border-gray-200 bg-gray-50 rounded-b-lg sm:rounded-b-xl">
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
            {warning.severity !== "info" && warning.warningType !== "trust_bonus" && (
              <Button
                variant="outline"
                onClick={() => {
                  window.open("/support", "_blank");
                }}
                className="w-full sm:flex-1 text-sm sm:text-base"
              >
                {t("warningPopup.contactSupport")}
              </Button>
            )}
            <Button
              onClick={() => {
                onDismiss(warning.id);
                onClose();
              }}
              className="w-full sm:flex-1 text-sm sm:text-base"
            >
              {warning.severity === "info" || warning.warningType === "trust_bonus"
                ? t("warningPopup.greatThanks")
                : t("warningPopup.iUnderstand")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WarningPopup;

